import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { AREAS, PHONE_DISPLAY, PHONE_TEL } from "../constants/content";

const SKYLINE =
    "https://images.unsplash.com/photo-1543962226-818f4301073f?crop=entropy&cs=srgb&fm=jpg&q=85";

export const ServiceAreas = () => (
    <section id="areas" className="relative overflow-hidden">
        <div className="absolute inset-0">
            <img
                src={SKYLINE}
                alt="Toronto skyline at dusk"
                className="h-full w-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundColor: "rgba(255,255,255,0.84)" }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
            <Reveal className="max-w-2xl">
                <Eyebrow>Toronto &amp; GTA service areas</Eyebrow>
                <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#141721] leading-tight">
                    Free scrap car towing,{" "}
                    <span className="text-[#FF3B30]">anywhere in the GTA.</span>
                </h2>
                <p className="mt-6 text-base text-[#565B6B] leading-relaxed">
                    From downtown Toronto to the edges of the Greater Toronto Area, our flatbed
                    drivers come to you — home, work or the side of the road. Tap your city for
                    local details.
                </p>
            </Reveal>

            <div className="mt-12 flex flex-wrap gap-3">
                {AREAS.map((area, i) => (
                    <Reveal key={area.label} delay={Math.min(i * 0.05, 0.5)}>
                        <Link
                            to={`/cash-for-cars/${area.slug}`}
                            data-testid={`area-pill-${i}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#E3E3DE] bg-white/80 backdrop-blur px-5 py-2.5 text-sm font-semibold text-[#141721]/90 transition-all duration-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                        >
                            <MapPin className="h-3.5 w-3.5 text-[#FF3B30]" strokeWidth={2.4} />
                            {area.label}
                        </Link>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.2}>
                <div className="mt-12 grid gap-6 lg:grid-cols-5">
                    <div
                        data-testid="service-area-map"
                        className="lg:col-span-3 relative overflow-hidden rounded-lg border border-[#E3E3DE] min-h-[360px]"
                    >
                        <iframe
                            title="Toronto Scrap Car Removal service area map — Toronto & GTA"
                            src="https://www.google.com/maps?q=Greater+Toronto+Area,+ON,+Canada&z=9&output=embed"
                            className="absolute inset-0 h-full w-full"
                            style={{
                                border: 0,
                                filter: "saturate(0.75)",
                            }}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                        <div className="absolute bottom-4 left-4 rounded-md bg-white/85 backdrop-blur-md border border-[#E3E3DE] px-4 py-2 pointer-events-none">
                            <span className="font-mono-ind text-[11px] uppercase tracking-widest text-[#FF3B30]">
                                Our towing zone — Toronto &amp; GTA
                            </span>
                        </div>
                    </div>

                    <div className="lg:col-span-2 flex flex-col justify-between gap-8 rounded-lg border border-[#E3E3DE] bg-white/85 backdrop-blur p-8">
                        <div>
                            <h3 className="font-display font-bold text-xl text-[#141721] tracking-tight">
                                We come to you — wherever you are
                            </h3>
                            <p className="mt-3 text-sm text-[#565B6B] leading-relaxed">
                                Home, work, or the side of the 401. One call and a flatbed is
                                dispatched to your location, anywhere on the map.
                            </p>
                            <div className="mt-6 space-y-3 font-mono-ind text-xs uppercase tracking-widest text-[#565B6B]">
                                <div className="flex justify-between border-b border-[#E3E3DE] pb-2">
                                    <span>Open</span>
                                    <span className="text-[#141721]">7 days a week</span>
                                </div>
                                <div className="flex justify-between border-b border-[#E3E3DE] pb-2">
                                    <span>Every day</span>
                                    <span className="text-[#141721]">7AM – 8PM</span>
                                </div>
                            </div>
                        </div>
                        <a
                            href={PHONE_TEL}
                            data-testid="areas-call-btn"
                            className="inline-flex items-center justify-center gap-3 rounded-md bg-[#FF3B30] px-8 py-4 font-display font-extrabold text-lg text-white uppercase tracking-wide transition-all duration-300 hover:bg-[#E0342B] hover:-translate-y-0.5"
                        >
                            <Phone className="h-5 w-5" strokeWidth={2.6} />
                            Call {PHONE_DISPLAY}
                        </a>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
